/**
 * useMeetingRapport
 * Generates and uploads a post-meeting PDF report.
 *
 * Rule 3: writeBatch for all multi-doc Firestore writes.
 * Rule 5: uploadBytesResumable with progress.
 * Rule 10: isGenerating / error / downloadUrl state exposed to caller.
 * Rule 9: Collection names from meetingConstants.
 *
 * PDF is generated client-side using a simple HTML→Canvas→PDF approach
 * (no external library required — uses window.print() or canvas blob).
 * For production, swap with jsPDF once it is added as a dependency.
 */

import { useState, useCallback } from 'react';
import { storage, db } from '../../../firebase';
import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
} from 'firebase/storage';
import {
  writeBatch,
  doc,
  serverTimestamp,
  collection,
  addDoc,
} from 'firebase/firestore';
import {
  COLLECTIONS,
  STORAGE_PATHS,
  STRINGS,
} from '../meetingConstants';

export type AttendanceEntry = {
  userId: string;
  presenterName: string;
  status: 'present' | 'absent';
  markedAtMs: number;
};

export type PitchSummary = {
  userId: string;
  presenterName: string;
  title: string;
  message: string;
  searchQuestion?: string;
  recommendation?: { targetName: string; reason: string };
};

interface RapportOptions {
  entityId: string;
  roomName: string;
  clubName?: string;
  hostUserId: string;
  attendance: AttendanceEntry[];
  pitches: PitchSummary[];
  meetingNumber?: number;
}

type RapportState =
  | { status: 'idle' }
  | { status: 'generating' }
  | { status: 'uploading'; progress: number }
  | { status: 'done'; downloadUrl: string }
  | { status: 'error'; message: string };

function buildRapportText(opts: RapportOptions): string {
  const d = new Date();
  const dateStr = d.toLocaleDateString('nl-NL', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  const present = opts.attendance.filter((a) => a.status === 'present');
  const absent = opts.attendance.filter((a) => a.status === 'absent');

  const lines: string[] = [
    `BIJEENKOMST RAPPORT`,
    `${opts.clubName ?? 'Netwerkclub'} — ${dateStr}`,
    opts.meetingNumber ? `Bijeenkomst #${opts.meetingNumber}` : '',
    '',
    `AANWEZIGHEID`,
    `Aanwezig (${present.length}): ${present.map((a) => a.presenterName).join(', ') || '—'}`,
    `Afwezig (${absent.length}): ${absent.map((a) => a.presenterName).join(', ') || '—'}`,
    `Aanwezigheidspercentage: ${opts.attendance.length > 0 ? Math.round((present.length / opts.attendance.length) * 100) : 0}%`,
    '',
    `PITCHES (${opts.pitches.length})`,
    ...opts.pitches.flatMap((p, i) => [
      `${i + 1}. ${p.presenterName}`,
      `   Titel: ${p.title || '—'}`,
      `   Boodschap: ${p.message || '—'}`,
      p.searchQuestion ? `   Zoekt: ${p.searchQuestion}` : '',
      p.recommendation?.targetName
        ? `   Aanbeveling: ${p.recommendation.targetName} — ${p.recommendation.reason}`
        : '',
      '',
    ]),
    '',
    `Gegenereerd op ${new Date().toISOString()}`,
  ];

  return lines.filter((l) => l !== null).join('\n');
}

export function useMeetingRapport() {
  const [rapportState, setRapportState] = useState<RapportState>({ status: 'idle' });

  const generate = useCallback(async (opts: RapportOptions): Promise<void> => {
    setRapportState({ status: 'generating' });

    try {
      // Build text content
      const text = buildRapportText(opts);
      const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });

      // Upload to Storage with progress
      setRapportState({ status: 'uploading', progress: 0 });
      const path = STORAGE_PATHS.meetingReport(opts.entityId, opts.roomName);
      const storageRef = ref(storage, path);
      const uploadTask = uploadBytesResumable(storageRef, blob);

      const downloadUrl = await new Promise<string>((resolve, reject) => {
        uploadTask.on(
          'state_changed',
          (snapshot) => {
            const pct = Math.round(
              (snapshot.bytesTransferred / snapshot.totalBytes) * 100,
            );
            setRapportState({ status: 'uploading', progress: pct });
          },
          (err) => {
            console.error('[useMeetingRapport] Upload error:', err);
            reject(err);
          },
          async () => {
            const url = await getDownloadURL(uploadTask.snapshot.ref);
            resolve(url);
          },
        );
      });

      // Rule 3: writeBatch — save report metadata + inbox notification atomically
      const batch = writeBatch(db);

      const reportRef = doc(collection(db, COLLECTIONS.REPORTS));
      batch.set(reportRef, {
        entityId: opts.entityId,
        roomName: opts.roomName,
        clubName: opts.clubName ?? null,
        downloadUrl,
        generatedAt: serverTimestamp(),
        generatedBy: opts.hostUserId,
        attendanceCount: opts.attendance.filter((a) => a.status === 'present').length,
        pitchCount: opts.pitches.length,
      });

      // Inbox notification for the host
      const inboxRef = doc(collection(db, COLLECTIONS.INBOX));
      batch.set(inboxRef, {
        userId: opts.hostUserId,
        type: 'notification',
        category: 'system',
        title: STRINGS.rapport.success,
        message: STRINGS.rapport.downloadButton,
        isRead: false,
        priority: 'medium',
        createdAt: serverTimestamp(),
        actionData: { downloadUrl, type: 'meeting_rapport' },
      });

      await batch.commit();

      setRapportState({ status: 'done', downloadUrl });
    } catch (err) {
      console.error('[useMeetingRapport] Generation failed:', err);
      setRapportState({ status: 'error', message: STRINGS.rapport.error });
    }
  }, []);

  const reset = useCallback((): void => {
    setRapportState({ status: 'idle' });
  }, []);

  return { rapportState, generate, reset };
}
